import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import { useMutation } from '@tanstack/react-query';
import { SectionSlug } from 'utils/assessmentSubmissionHelpers';

export interface SubmitSectionsArgs {
  assessmentId: string;
  sectionedPayloads: Partial<Record<SectionSlug, Record<string, any>>>;
}

const SECTION_LABELS: Record<SectionSlug, string> = {
  'soft-skill': 'Soft Skill',
  personality: 'Personality',
  'work-style': 'Work Style',
  'work-motivators': 'Work Motivators',
  'basic-expectations': 'Basic Expectations',
  'work-qualities': 'Work Qualities',
  'domain-knowledge': 'Domain Knowledge',
};

const SECTION_ENDPOINTS: Record<SectionSlug, string> = {
  'soft-skill': 'soft-skill',
  personality: 'personality',
  'work-style': 'work-style',
  'work-motivators': 'work-motivators',
  'basic-expectations': 'basic-expectations',
  'work-qualities': 'work-qualities',
  'domain-knowledge': 'domain-knowledge',
};

/**
 * Fires one POST per section to /api/v2/candidate/<section>/{assessmentId}/ in parallel.
 * Promise.all fails fast — if any section rejects, the whole mutation rejects.
 */
export const useSubmitAssessmentSections = () => {
  const { openToast } = useToast();

  return useMutation({
    mutationFn: async ({ assessmentId, sectionedPayloads }: SubmitSectionsArgs) => {
      const entries = Object.entries(sectionedPayloads) as Array<[SectionSlug, Record<string, any>]>;
      const calls = entries.map(async ([section, body]) => {
        try {
          const endpoint = SECTION_ENDPOINTS[section] || section;
          const res = await apiClient.post(`/candidate/${endpoint}/${assessmentId}/`, body);
          return { section, data: res.data };
        } catch (err: any) {
          err._section = section;
          throw err;
        }
      });
      const results = await Promise.all(calls);
      return results.reduce(
        (acc, { section, data }) => ({ ...acc, [section]: data }),
        {} as Record<string, any>
      );
    },
    onError: (err: any) => {
      const section = err?._section as SectionSlug | undefined;
      const sectionLabel = section ? SECTION_LABELS[section] : 'assessment';
      const serverMsg =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.response?.data?.message;
      openToast({
        show: true,
        heading: 'Submission Failed',
        text: serverMsg || `Failed to submit ${sectionLabel} responses`,
        type: 'error',
        timeOut: 5000,
      });
    },
  });
};

/** @deprecated kept as an alias so existing imports compile. Prefer useSubmitAssessmentSections. */
export const useUpdateAssessmentResponse = useSubmitAssessmentSections;

export type AssessmentResponsePayload = Partial<Record<SectionSlug, Record<string, any>>>;
